import {
  Component,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { FileUploadError } from './file-upload.error';
import { TextButtonComponent } from '../text-button/text-button.component';
import { DragOverDirective } from '../../directives/drag-over.directive';

@Component({
  selector: 'app-file-upload',
  imports: [
    CommonModule,
    MatIconModule,
    DragOverDirective,
    TextButtonComponent,
    TranslocoDirective,
  ],
  templateUrl: './file-upload.component.html',
})
export class FileUploadComponent {
  private readonly snackbarService = inject(SnackbarService);

  icon = input<string>('');
  acceptedFile = input.required<string>();
  sizeClasses = input<string>('');
  fileSelected = output<string>();

  readonly inputElement =
    viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
  fileName: string;

  private readonly maxFileSize: Record<string, number> = {
    /* eslint-disable @typescript-eslint/naming-convention */
    'text/csv': 300 * 1024, // 300 KB in bytes
    'application/vnd.ms-excel': 2 * 1024 * 1024, // 2 MB for Excel files
    'image/png': 10 * 1024 * 1024, // 10 MB
    /* eslint-enable @typescript-eslint/naming-convention */
  };

  private readonly signatureBytes: Partial<Record<string, number[]>> = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'image/png': [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  };

  async onFileSelected() {
    const file = this.getFile();
    if (!file) {
      console.error('File is missing');
      return;
    }
    this.fileName = file.name;
    try {
      this.validateFileTypeAndSize(file);

      const signature = this.signatureBytes[file.type];
      if (signature) {
        await this.validateMagicBytes(file, signature);
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        this.fileSelected.emit(dataUrl);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      this.inputElement().nativeElement.value = '';
      if (error instanceof FileUploadError) {
        this.snackbarService.error(
          'general.fileUpload.errors.' + error.message,
          false,
          error.variableData,
        );
      }
    }
  }

  private async validateMagicBytes(
    file: File,
    signature: number[],
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target || !(e.target.result instanceof ArrayBuffer)) {
          reject(new FileUploadError('FILE_CANNOT_BE_READ'));
          return;
        }
        const bytes = new Uint8Array(e.target.result);
        const match = signature.every((b, i) => bytes[i] === b);
        if (match) {
          resolve();
        } else {
          reject(
            new FileUploadError('INVALID_FILE_TYPE', {
              filetype: this.acceptedFile(),
            }),
          );
        }
      };
      reader.onerror = () => {
        reject(new FileUploadError('FILE_CANNOT_BE_READ'));
      };
      reader.readAsArrayBuffer(file.slice(0, signature.length));
    });
  }

  reset(): void {
    this.inputElement().nativeElement.value = '';
    this.fileName = '';
  }

  private getFile(): File | undefined {
    const el = this.inputElement().nativeElement;
    if (!el.files?.length) {
      return undefined;
    }
    const [file] = Array.from(el.files);
    return file;
  }

  public validateFileTypeAndSize(file: File): void {
    const maxSize = this.maxFileSize[file.type];
    if (!this.acceptedFile().includes(file.type)) {
      throw new FileUploadError('INVALID_FILE_TYPE', {
        filetype: this.acceptedFile(),
      });
    }

    if (file.size > maxSize) {
      throw new FileUploadError('INVALID_FILE_SIZE', {
        acceptedFileSize: maxSize / (1024 * 1024) + 'MB',
      });
    }
  }
}
